import {
  AMCCMSPlan,
  CohortContentType,
  CohortContentsTypes,
  Story,
} from '@/actions/fetchAmcPlanDetails';
import { AMCPlan } from '@/actions/fetchAmcPlans';
import is from './is';
import { AssetsData } from '@/types/asset';
import getSingleString from './string';
import SessionStorage from './sessionStorage';
import { AMC_SERVICE_SESSION } from '@/constants/amc';
import { DEVICE_STATUS } from '@/constants/app';

// abs(Cohort-cms) >= abs(daysToExpire-api)
export const getCohortContent = (
  content: CohortContentsTypes,
  expiryStatus: { status: 'IW' | 'OW' | 'IC' | 'OC'; daysToExpire: number },
): CohortContentType => {
  const { status, daysToExpire } = expiryStatus || {};
  const cohorts = content?.[status];
  let cohortContent: CohortContentType = {
    days: 0,
    title: '',
    subTitle: null,
    savingText: null,
    visiblePlanTypes: null,
  };

  if (cohorts && is.not.empty.array(cohorts)) {
    for (const cohort of cohorts) {
      if (Math.abs(Number(cohort.days)) >= Math.abs(daysToExpire)) {
        cohortContent = cohort;
        break;
      }
    }
  }
  return cohortContent;
};

export const mergePlans = (amcPlans: AMCPlan[], cmsPlans: AMCCMSPlan[]) => {
  const map = amcPlans.reduce((acc: { [key: string]: AMCPlan }, amcPlan) => {
    amcPlan.tier = amcPlan.tier.toUpperCase();
    acc[amcPlan.tier.toUpperCase()] = amcPlan;
    return acc;
  }, {});

  return cmsPlans.map((cmsPlan) => ({
    ...cmsPlan.attributes,
    ...(map[cmsPlan.attributes.tier] || {}),
  }));
};

export const getTierWithRecommendedFlag = (plans: any[]) => {
  let recommendedTier = '';
  if (is.not.empty.array(plans)) {
    plans.forEach((plan) => {
      if (plan.recommended) {
        recommendedTier = plan.tier.toLowerCase();
      }
    });
  }
  return recommendedTier;
};

export const getSelectedDevice = (devices: AssetsData[], id?: string | string[]): AssetsData => {
  return devices?.find(({ id: deviceId }) => getSingleString(id) === deviceId) ?? devices?.[0];
};

export const getSelectedIRDevice = (devices: any[], reqId?: string | string[]): any => {
  return devices?.find((device: any) => device.msdyn_servicerequest?.ticketnumber === reqId);
};

export const isApplicableForAMCUpSell = (
  appConfig: any,
  serviceType: string,
  device: AssetsData,
  isRescheduleFlow?: string | boolean,
) => {
  const { isRental, expiryStatus, upcomingAMCPlan, technology } = device || {};
  const { status, daysToExpire } = expiryStatus || {};
  const amcUpsell = appConfig?.cohortRule?.amcUpsell;
  if (
    !isRescheduleFlow &&
    upcomingAMCPlan?.agreementId === '' &&
    !isRental &&
    status &&
    daysToExpire !== undefined &&
    amcUpsell &&
    !['reinstall', 'uninstall', 'dismantling'].includes(serviceType.toLowerCase()) &&
    ['RO', 'UV'].includes(technology)
  ) {
    const absoluteDaysToExpire = Math.abs(daysToExpire);
    if (Math.abs(amcUpsell[status]) >= absoluteDaysToExpire) {
      return true;
    }
    return false;
  }
  return false;
};

export const isServiceUpsellFlow = () => Boolean(SessionStorage.get(AMC_SERVICE_SESSION));

export const getStories = (stories: Story[]) => {
  if (!stories) return [];
  return stories?.map((story: any) => {
    return {
      url: story.attributes.url,
      type: 'video',
      thumbnail: story.attributes.alternativeText,
      preloadResource: true,
    };
  });
};

export const isApplicableForNCEMI = (NCEMIPriceLimit: number, price: number) => {
  return NCEMIPriceLimit ? Boolean(price >= NCEMIPriceLimit) : false;
};

export const blockAMCJourney = ({ isRental, upcomingAMCPlan, technology, expiryStatus }: any) => {
  const { status, isAboutToExpire } = expiryStatus;
  return (
    isRental ||
    upcomingAMCPlan?.agreementId !== '' ||
    !['RO', 'UV'].includes(technology) ||
    (status === DEVICE_STATUS.IC && !isAboutToExpire) ||
    (status === DEVICE_STATUS.IW && !isAboutToExpire)
  );
};

export const getPlanEndDate = (daysToExpire: number, planDuration: number) => {
  const currentDate = new Date();
  return new Date(
    currentDate.setDate(currentDate.getDate() + daysToExpire + planDuration * 365.25),
  );
};
